import { 
  Brain, 
  Target, 
  Zap, 
  Shield, 
  TrendingUp, 
  Users, 
  BarChart3, 
  Globe, 
  Database, 
  Smartphone, 
  Lock, 
  Gauge,
  Activity,
  Rocket,
  Eye,
  Network,
  CheckCircle,
  Sparkles,
  Star,
  Settings
} from 'lucide-react';
import { ProductFeatureConfig } from './types';

export const crownpeakBeautyDxpConfig: ProductFeatureConfig = {
  keyBenefits: [
    {
      icon: Brain,
      title: 'AI-Powered Content Intelligence',
      description: 'Advanced AI algorithms automatically optimize beauty content across all digital touchpoints for maximum engagement and conversion.',
      metrics: '40% increase in content engagement, 60% faster content creation',
      color: 'from-purple-500 to-indigo-600'
    },
    {
      icon: Target,
      title: 'Personalized Beauty Experiences',
      description: 'Deliver hyper-personalized beauty recommendations and content based on individual skin profiles, preferences, and behavior patterns.',
      metrics: '85% improvement in customer satisfaction, 3x higher conversion rates',
      color: 'from-pink-500 to-rose-600'
    },
    {
      icon: TrendingUp,
      title: 'Real-Time Beauty Trend Analytics',
      description: 'Monitor and capitalize on emerging beauty trends with real-time analytics and predictive modeling to stay ahead of the competition.',
      metrics: '25% faster trend adoption, 200% increase in viral content creation',
      color: 'from-blue-500 to-cyan-600'
    },
    {
      icon: Users,
      title: 'Omnichannel Customer Journey',
      description: 'Seamlessly connect online and offline beauty experiences with unified customer profiles and consistent messaging across all channels.',
      metrics: '90% customer journey visibility, 45% reduction in acquisition costs',
      color: 'from-green-500 to-emerald-600'
    },
    {
      icon: Sparkles,
      title: 'Dynamic Beauty Content Creation',
      description: 'Automated beauty content generation with seasonal campaigns, product launches, and influencer collaboration management.',
      metrics: '300% increase in content production efficiency',
      color: 'from-amber-500 to-orange-600'
    },
    {
      icon: Star,
      title: 'Beauty Brand Identity Management',
      description: 'Comprehensive brand consistency across all digital touchpoints with automated guideline enforcement and visual identity control.',
      metrics: '95% brand consistency improvement',
      color: 'from-violet-500 to-purple-600'
    }
  ],
  advancedCapabilities: [
    {
      icon: BarChart3,
      title: 'Advanced Beauty Analytics Dashboard',
      description: 'Comprehensive analytics platform with beauty-specific KPIs, customer lifetime value tracking, and predictive beauty trend forecasting.',
      metrics: 'Real-time insights from 50+ beauty-specific metrics',
      color: 'from-orange-500 to-red-600'
    },
    {
      icon: Globe,
      title: 'Global Beauty Market Localization',
      description: 'Automatically adapt beauty content, products, and campaigns for different markets with cultural sensitivity and regulatory compliance.',
      metrics: 'Support for 40+ countries, 95% localization accuracy',
      color: 'from-teal-500 to-blue-600'
    },
    {
      icon: Database,
      title: 'Beauty Product Information Management',
      description: 'Centralized PIM system specifically designed for beauty products with ingredient tracking, regulatory compliance, and batch management.',
      metrics: '99.9% product data accuracy, 70% faster product launches',
      color: 'from-indigo-500 to-purple-600'
    },
    {
      icon: Smartphone,
      title: 'Mobile-First Beauty Commerce',
      description: 'Optimized mobile shopping experiences with AR try-on, voice search, and social commerce integration for beauty products.',
      metrics: '80% of traffic on mobile, 4.8/5 mobile app rating',
      color: 'from-pink-500 to-purple-600'
    },
    {
      icon: Zap,
      title: 'Real-Time Personalization Engine',
      description: 'Dynamic content and product personalization based on real-time behavior analysis and beauty preference learning.',
      metrics: '92% personalization accuracy',
      color: 'from-rose-500 to-red-600'
    },
    {
      icon: Settings,
      title: 'Advanced Integration Ecosystem',
      description: 'Seamless integration with beauty tools, CRM systems, marketing platforms, and social media management solutions.',
      metrics: 'Connect with 200+ beauty platforms',
      color: 'from-green-500 to-teal-600'
    }
  ],
  securityPerformance: [
    {
      icon: Target,
      title: 'Compliance Excellence Framework',
      description: 'Automated compliance monitoring for SOC 2, ISO 27001, GDPR, and industry-specific regulations',
      metrics: 'Complete regulatory compliance',
      color: 'from-indigo-500 to-purple-600'
    },
    {
      icon: Activity,
      title: 'Security Performance Analytics',
      description: 'Advanced security metrics with threat landscape analysis and security posture optimization',
      metrics: 'Optimized security posture',
      color: 'from-yellow-500 to-amber-600'
    },
    {
      icon: Rocket,
      title: 'Scalable Security Infrastructure',
      description: 'Enterprise-grade security platform supporting unlimited users with global threat intelligence',
      metrics: 'Unlimited security scale',
      color: 'from-emerald-500 to-green-600'
    },
    {
      icon: Shield,
      title: 'Advanced Firewall & DDoS Protection',
      description: 'Next-generation firewall with AI-powered DDoS mitigation and intrusion prevention systems',
      metrics: '99.99% attack mitigation',
      color: 'from-red-500 to-pink-600'
    },
    {
      icon: Lock,
      title: 'Multi-Factor Authentication Suite',
      description: 'Enterprise SSO with biometric authentication, hardware tokens, and risk-based access controls',
      metrics: 'Zero-trust access control',
      color: 'from-blue-500 to-indigo-600'
    },
    {
      icon: Eye,
      title: 'Security Information & Event Management',
      description: 'Real-time SIEM with behavioral analytics, threat hunting, and automated incident correlation',
      metrics: 'Real-time threat visibility',
      color: 'from-purple-500 to-pink-600'
    },
    {
      icon: Database,
      title: 'Data Loss Prevention & Backup',
      description: 'Automated data classification, leakage prevention, and secure backup with point-in-time recovery',
      metrics: 'Zero data loss guarantee',
      color: 'from-green-500 to-emerald-600'
    },
    {
      icon: Network,
      title: 'Network Segmentation & Monitoring',
      description: 'Micro-segmentation with network traffic analysis and anomaly detection for lateral movement prevention',
      metrics: 'Complete network visibility',
      color: 'from-orange-500 to-red-600'
    },
    {
      icon: CheckCircle,
      title: 'Continuous Security Assessment',
      description: 'Automated vulnerability scanning, security testing, and continuous compliance validation',
      metrics: 'Proactive security validation',
      color: 'from-cyan-500 to-blue-600'
    }
  ]
};